import * as React from 'react'
import {
  type AnimationOptions,
  type HTMLMotionProps,
  motion,
  MotionConfigContext,
  useIsPresent,
} from 'motion/react'

import { useIsFirstRender } from '../../hooks/useIsFirstRender'
import { useAnimateDigit } from './hooks/useAnimateDigit'
import type { Justify } from './types'
import styles from './Digit.module.css'

/**
 * Props for the Digit component
 * Extends motion span props but omits children as they're handled internally
 */
type DigitProps = Omit<HTMLMotionProps<'span'>, 'children'> & {
  value: number
  initialValue?: number
  layoutDependency?: any
  justify?: Justify
}

const DigitStack = ({ value }: { value: number }): JSX.Element => {
  const digitsBelow = React.useMemo(() => {
    if (value === 0) return null
    return Array.from({ length: value }, (_, i) => (
      <span key={i} className={styles.digit}>
        {i}
      </span>
    ))
  }, [value])

  const digitsAbove = React.useMemo(() => {
    if (value === 9) return null
    return Array.from({ length: 9 - value }, (_, i) => (
      <span key={i} className={styles.digit}>
        {value + i + 1}
      </span>
    ))
  }, [value])

  return (
    <>
      {digitsBelow && (
        <span className={`${styles.digitStack} ${styles.digitStackBottom}`}>
          {digitsBelow}
        </span>
      )}
      {/* Render current digit */}
      <span className={styles.digit}>{value}</span>
      {digitsAbove && (
        <span className={`${styles.digitStack} ${styles.digitStackTop}`}>
          {digitsAbove}
        </span>
      )}
    </>
  )
}

const validateDigit = (val: number): void => {
  if (val < 0 || val > 9) {
    console.warn(`Digit received invalid value: ${val}. Expected 0-9.`)
  }
}

/**
 * Digit component that renders a single digit with smooth transitions
 * Uses FLIP animation technique for smooth number changes
 */
export const Digit = React.forwardRef<HTMLSpanElement, DigitProps>(
  (
    { value: _value, initialValue: _initialValue = _value, ...rest },
    _ref,
  ): JSX.Element => {
    const { transition } = React.useContext(MotionConfigContext)
    const initialValue = React.useRef(_initialValue).current
    const { isFirstRender } = useIsFirstRender()

    const animateRef = React.useRef<HTMLSpanElement>(null) // used for animating with FLIP
    const widthRef = React.useRef<HTMLSpanElement>(null) // used for measuring width
    // Expose only the width measurement to parent components
    // This allows NumberSection to measure and animate the digit's width
    // without accessing the component's internal state
    React.useImperativeHandle(_ref, () => widthRef.current!, [])

    const isPresent = useIsPresent()
    const displayValue = isPresent ? _value : 0

    if (process.env.NODE_ENV !== 'production') {
      validateDigit(displayValue)
    }

    const { width } = useAnimateDigit(
      animateRef,
      widthRef,
      displayValue,
      initialValue,
      transition as AnimationOptions,
      isFirstRender,
    )

    return (
      <motion.span
        {...rest}
        ref={widthRef}
        layout="position"
        data-state={isPresent ? undefined : 'exiting'}
        className={styles.digitContainer}
        style={{ '--width': width } as React.CSSProperties}
      >
        <span ref={animateRef} className={styles.digitStackWrapper}>
          <DigitStack value={displayValue} />
        </span>
      </motion.span>
    )
  },
)

Digit.displayName = 'Digit'
